import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { digitalOcean } from "@/integrations/digitalocean/client";
import { Button } from "@/components/ui/button";
import { Music, CheckCircle, XCircle, Clock, Play } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { LeaderboardBadge } from "@/components/LeaderboardBadge";
import { useLeaderboardPosition } from "@/hooks/useLeaderboardPosition";
import { getDefaultCoverImage } from "@/utils/defaultCoverImage";

// Helper component to show leaderboard badge for an artist
function ArtistLeaderboardBadge({ artistId }) {
  const { data: position } = useLeaderboardPosition(artistId);
  return position ? <LeaderboardBadge position={position} /> : null;
}

export default function CuratorDashboard({ userId }) {
  const navigate = useNavigate();
  const [updatingId, setUpdatingId] = useState(null);

  const { data: submissions = [], refetch } = useQuery({
    queryKey: ["curator-submissions", userId],
    queryFn: async () => {
      // Only fetch submissions where this user is the curator
      const { data, error } = await digitalOcean
        .from("playlist_submissions")
        .select(`
          *,
          tracks (
            id,
            title,
            artist_name,
            artist_id,
            cover_image_url,
            audio_url,
            genres
          ),
          profiles:user_id (
            username,
            avatar_url
          )
        `)
        .eq("curator_id", userId)
        .order("created_at", { ascending: false });
      
      if (error) {
        console.error("Error fetching curator submissions:", error);
        throw error;
      }
      console.log("[CuratorDashboard] Submissions for curator:", data);
      return data || [];
    },
    enabled: !!userId,
  });

  const updateSubmissionStatus = async (submissionId, newStatus) => {
    setUpdatingId(submissionId);
    try {
      const { error } = await digitalOcean
        .from("playlist_submissions")
        .update({ status: newStatus })
        .eq("id", submissionId);

      if (error) throw error;

      toast.success(`Submission ${newStatus}`);
      refetch();
    } catch (error) {
      console.error("Error updating submission:", error);
      toast.error("Failed to update submission");
    } finally {
      setUpdatingId(null);
    }
  };

  const pendingSubmissions = submissions.filter(s => s.status === "pending");
  const approvedSubmissions = submissions.filter(s => s.status === "approved");
  const rejectedSubmissions = submissions.filter(s => s.status === "rejected");

  const SubmissionCard = ({ submission, showActions = true }) => {
    const track = submission.tracks;
    const submitter = submission.profiles;

    return (
      <div className="neuro-flat rounded-2xl p-4 mb-3">
        <div className="flex items-start gap-4">
          <img
            src={track?.cover_image_url || getDefaultCoverImage(track?.id)}
            alt={track?.title || "Track"}
            className="w-20 h-20 rounded-xl object-cover"
          />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-[#d0d0d0] mb-1">{track?.title}</h3>
            <p className="text-xs text-[#808080] mb-2 flex items-center gap-1.5">
              {track?.artist_name}
              {track?.artist_id && <ArtistLeaderboardBadge artistId={track.artist_id} />}
            </p>
            <div className="flex items-center gap-2 mb-2">
              <img
                src={submitter?.avatar_url || "/placeholder.svg"}
                alt={submitter?.username}
                className="w-6 h-6 rounded-full"
              />
              <span className="text-xs text-[#808080]">by {submitter?.username}</span>
            </div>
            {track?.genres && track.genres.length > 0 && (
              <div className="flex gap-2 flex-wrap mb-3">
                {track.genres.slice(0, 3).map((genre, idx) => (
                  <span
                    key={idx}
                    className="text-xs px-2 py-1 rounded-lg bg-[#1a1a1a] text-[#808080]"
                  >
                    {genre}
                  </span>
                ))}
              </div>
            )}
            {showActions && (
              <div className="flex gap-2">
                <Button
                  onClick={() => navigate(createPageUrl("TrackDetails") + `?id=${track.id}`)}
                  size="sm"
                  variant="outline"
                  className="neuro-base active:neuro-pressed text-xs"
                >
                  <Play className="w-3 h-3 mr-1" />
                  Listen
                </Button>
                <Button
                  onClick={() => updateSubmissionStatus(submission.id, "approved")}
                  disabled={updatingId === submission.id}
                  size="sm"
                  className="neuro-base active:neuro-pressed text-xs bg-green-900/20 text-green-400"
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Approve
                </Button>
                <Button
                  onClick={() => updateSubmissionStatus(submission.id, "rejected")}
                  disabled={updatingId === submission.id}
                  size="sm"
                  className="neuro-base active:neuro-pressed text-xs bg-red-900/20 text-red-400"
                >
                  <XCircle className="w-3 h-3 mr-1" />
                  Reject
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Pending Submissions */}
      <div className="neuro-base rounded-3xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Clock className="w-6 h-6 text-[#a0a0a0]" />
          <h2 className="text-xl font-light text-[#d0d0d0]">Pending Submissions</h2>
          <span className="neuro-pressed px-3 py-1 rounded-xl text-xs text-[#a0a0a0]">
            {pendingSubmissions.length}
          </span>
        </div>
        {pendingSubmissions.length === 0 ? (
          <div className="text-center py-8">
            <Music className="w-12 h-12 mx-auto text-[#606060] mb-3" />
            <p className="text-[#808080]">No pending submissions</p>
          </div>
        ) : (
          pendingSubmissions.map((submission) => (
            <SubmissionCard key={submission.id} submission={submission} />
          ))
        )}
      </div>

      {/* Approved Submissions */}
      <div className="neuro-base rounded-3xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <CheckCircle className="w-6 h-6 text-green-400" />
          <h2 className="text-xl font-light text-[#d0d0d0]">Approved</h2>
          <span className="neuro-pressed px-3 py-1 rounded-xl text-xs text-[#a0a0a0]">
            {approvedSubmissions.length}
          </span>
        </div>
        {approvedSubmissions.length === 0 ? (
          <p className="text-[#808080] text-sm">No approved submissions yet</p>
        ) : (
          approvedSubmissions.slice(0, 5).map((submission) => (
            <SubmissionCard key={submission.id} submission={submission} showActions={false} />
          ))
        )}
      </div>

      {/* Rejected Submissions */}
      <div className="neuro-base rounded-3xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <XCircle className="w-6 h-6 text-red-400" />
          <h2 className="text-xl font-light text-[#d0d0d0]">Rejected</h2>
          <span className="neuro-pressed px-3 py-1 rounded-xl text-xs text-[#a0a0a0]">
            {rejectedSubmissions.length}
          </span>
        </div>
        {rejectedSubmissions.length === 0 ? (
          <p className="text-[#808080] text-sm">No rejected submissions yet</p>
        ) : (
          rejectedSubmissions.slice(0, 5).map((submission) => (
            <SubmissionCard key={submission.id} submission={submission} showActions={false} />
          ))
        )}
      </div>
    </div>
  );
}
